import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { paymentAPI } from '@matix/api-client';
import type { PaymentType } from '@matix/types';
import { api, type LinkSentVia } from '../../lib/api';
import type { AppointmentPayment, SessionPayments } from '../../lib/types';
import { fmtMoney, moneyPrecision, roundM } from '../../lib/currency';
import { useLang } from '../../lib/lang';
import { useErrMsg } from '../../lib/errors';

/**
 * Collect a deposit on an appointment before the invoice exists.
 *
 * Two ways the money arrives, and the dialog is honest about which one it is:
 *
 *   - A method the desk can take itself (cash, card on the terminal, …):
 *     recorded as PAID the moment it is confirmed here.
 *   - Tap: the POS asks Tap's Invoices API for a payment link and TAP SENDS THE
 *     SMS to the customer. Nothing is paid yet; the dialog polls until Tap says
 *     PAID, or the receptionist closes it (the server keeps checking).
 *
 * Every figure shown is the server's: total, paid, pending and remaining come
 * back on every reply, and the amount is validated server-side against the
 * remaining balance.
 */

const POLL_INTERVAL_MS = 3_000;
const TAP_CODE = -3;

/** Dial codes offered in the selector; the server's default is always present. */
const DIAL_CHOICES: Array<{ code: string; en: string; ar: string }> = [
  { code: '965', en: 'Kuwait', ar: 'الكويت' },
  { code: '966', en: 'Saudi Arabia', ar: 'السعودية' },
  { code: '971', en: 'UAE', ar: 'الإمارات' },
  { code: '973', en: 'Bahrain', ar: 'البحرين' },
  { code: '974', en: 'Qatar', ar: 'قطر' },
  { code: '968', en: 'Oman', ar: 'عُمان' },
  { code: '20', en: 'Egypt', ar: 'مصر' },
  { code: '962', en: 'Jordan', ar: 'الأردن' },
  { code: '964', en: 'Iraq', ar: 'العراق' },
  { code: '961', en: 'Lebanon', ar: 'لبنان' },
];
const KNOWN_DIAL = [...DIAL_CHOICES.map((c) => c.code), '967', '963', '970', '218', '216', '212', '213', '249', '90', '44', '1']
  .sort((a, b) => b.length - a.length);

/**
 * Split a stored number into the selector's two fields. "+965 9527 9011" and
 * "0096595279011" keep their own code; a bare "95279011" takes the server's
 * default for this customer/tenant.
 */
function splitForDialog(raw: string | null, defaultDialCode: string): { dialCode: string; number: string } {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) return { dialCode: defaultDialCode, number: '' };
  const hadPrefix = trimmed.startsWith('+') || trimmed.startsWith('00');
  let digits = trimmed.replace(/\D/g, '');
  if (trimmed.startsWith('00')) digits = digits.slice(2);
  if (hadPrefix) {
    const cc = KNOWN_DIAL.find((c) => digits.startsWith(c));
    if (cc) return { dialCode: cc, number: digits.slice(cc.length).replace(/^0+/, '') };
  }
  return { dialCode: defaultDialCode, number: digits.replace(/^0+/, '') };
}

export interface DepositDialogProps {
  sessionId: number;
  summary: SessionPayments;
  customerName: string | null;
  customerPhone: string | null;
  onClose: () => void;
  /** Every server reply carries the fresh summary; the caller keeps the screen in step. */
  onChanged: (summary: SessionPayments) => void;
}

export function DepositDialog({
  sessionId,
  summary: initial,
  customerName,
  customerPhone,
  onClose,
  onChanged,
}: DepositDialogProps): JSX.Element {
  const { t, lang } = useLang();
  const errMsg = useErrMsg();

  const typesQ = useQuery({
    queryKey: ['payment-types'],
    queryFn: () => paymentAPI.listTypes(),
    staleTime: 10 * 60 * 1000,
  });

  const [summary, setSummary] = useState<SessionPayments>(initial);
  const [amountStr, setAmountStr] = useState('');
  // The mobile number as dial code + local digits. Tap validates the local
  // part against the country code, so the code is an explicit choice here —
  // defaulted by the server from the customer's / tenant's country, never
  // guessed from the currency (a SAR tenant in Kuwait broke on exactly that).
  const initialPhone = useMemo(() => splitForDialog(customerPhone, initial.dial_code), [customerPhone, initial.dial_code]);
  const [dialCode, setDialCode] = useState(initialPhone.dialCode);
  const [localNumber, setLocalNumber] = useState(initialPhone.number);
  const dialChoices = useMemo(
    () => (DIAL_CHOICES.some((c) => c.code === dialCode) ? DIAL_CHOICES : [{ code: dialCode, en: dialCode, ar: dialCode }, ...DIAL_CHOICES]),
    [dialCode],
  );
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /** The Tap link just created, while this dialog watches it. */
  const [watching, setWatching] = useState<{ payment: AppointmentPayment; via: LinkSentVia | null } | null>(null);
  const [copied, setCopied] = useState(false);

  const step = useMemo(() => String(10 ** -moneyPrecision()), []);
  const entered = roundM(parseFloat(amountStr) || 0);
  /** What may still be asked for: remaining, less links already out. */
  const askable = roundM(Math.max(0, summary.remaining - summary.pending));

  const allTypes = typesQ.data?.payment_types ?? [];
  // Methods the desk takes itself. Multi (0), Unpaid (-1) and Credit (2) are
  // not money in hand; the hosted gateways other than Tap (-4 … -7) have no
  // link flow here, so they are not offered for a deposit either.
  const deskMethods = allTypes.filter((m) => m.code > 0 && m.code !== 2);
  const tapType = allTypes.find((m) => m.code === TAP_CODE) ?? null;

  function publish(s: SessionPayments): void {
    setSummary(s);
    onChanged(s);
  }

  // ── Poll the link we just sent ──────────────────────────────────────────
  const watchingRef = useRef(watching);
  watchingRef.current = watching;
  useEffect(() => {
    if (!watching || watching.payment.status !== 'pending') return;
    let stopped = false;
    const tick = async (): Promise<void> => {
      try {
        const s = await api.payments(sessionId, { poll: true });
        if (stopped || !s) return;
        publish(s);
        const row = s.payments.find((p) => p.id === watchingRef.current?.payment.id);
        if (row) setWatching((w) => (w ? { ...w, payment: row } : w));
      } catch {
        /* transient — next tick */
      }
    };
    const timer = setInterval(() => void tick(), POLL_INTERVAL_MS);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watching?.payment.id, watching?.payment.status, sessionId]);

  async function create(method: 'manual' | 'tap_invoice', type: PaymentType): Promise<void> {
    if (saving) return;
    setError(null);
    setNotice(null);
    if (entered <= 0) {
      setNotice(t({ en: 'Enter the deposit amount first.', ar: 'أدخل مبلغ العربون أولاً.' }));
      return;
    }
    if (entered > askable + 1e-9) {
      setNotice(t({
        en: `The deposit cannot exceed ${fmtMoney(askable)}.`,
        ar: `لا يمكن أن يتجاوز العربون ${fmtMoney(askable)}.`,
      }));
      return;
    }
    const digits = localNumber.replace(/\D/g, '').replace(/^0+/, '');
    if (method === 'tap_invoice' && digits.length < 6) {
      setNotice(t({
        en: "Enter the customer's mobile number — Tap sends the payment link by SMS.",
        ar: 'أدخل رقم جوال العميل — ترسل Tap رابط الدفع عبر رسالة نصية.',
      }));
      return;
    }
    const phone = `+${dialCode}${digits}`;
    setSaving(true);
    try {
      const r = await api.createPayment(sessionId, {
        amount: entered,
        payment_type_code: type.code,
        method,
        ...(method === 'tap_invoice' ? { phone } : {}),
        ...(method === 'tap_invoice' && email.trim() ? { email: email.trim() } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      publish(r.summary);
      setAmountStr('');
      if (method === 'manual') {
        setNotice(t({
          en: `${fmtMoney(r.payment.amount)} recorded on ${type.name}.`,
          ar: `تم تسجيل ${fmtMoney(r.payment.amount)} على ${type.name}.`,
        }));
      } else {
        setWatching({ payment: r.payment, via: r.link_sent_via });
      }
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setSaving(false);
    }
  }

  async function resend(): Promise<void> {
    if (!watching || saving) return;
    setSaving(true);
    setError(null);
    try {
      const r = await api.remindPayment(sessionId, watching.payment.id);
      publish(r.summary);
      setNotice(
        r.sent_via === 'tap_sms'
          ? t({ en: 'Tap sent the link again.', ar: 'أعادت Tap إرسال الرابط.' })
          : r.sent_via === 'pos_sms'
            ? t({ en: 'The link was sent again by SMS.', ar: 'أُعيد إرسال الرابط عبر رسالة نصية.' })
            : t({ en: 'Could not resend — share the link below instead.', ar: 'تعذّرت إعادة الإرسال — شارك الرابط أدناه بدلاً من ذلك.' }),
      );
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setSaving(false);
    }
  }

  async function cancelLink(): Promise<void> {
    if (!watching || saving) return;
    setSaving(true);
    setError(null);
    try {
      const s = await api.cancelPayment(sessionId, watching.payment.id);
      publish(s);
      setWatching(null);
      setNotice(t({ en: 'The payment link was cancelled.', ar: 'تم إلغاء رابط الدفع.' }));
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setSaving(false);
    }
  }

  async function copyLink(): Promise<void> {
    if (!watching?.payment.payment_url) return;
    try {
      await navigator.clipboard.writeText(watching.payment.payment_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — the link is visible to select */
    }
  }

  const quick = useMemo(() => {
    const pct = [25, 50, 100].map((p) => roundM((askable * p) / 100));
    return pct.filter((v, i, arr) => v > 0 && arr.indexOf(v) === i);
  }, [askable]);

  const viaText = (via: LinkSentVia | null): string => {
    switch (via) {
      case 'tap_sms':
        return t({ en: 'Tap sent the payment link by SMS', ar: 'أرسلت Tap رابط الدفع عبر رسالة نصية' });
      case 'tap_sms_email':
        return t({ en: 'Tap sent the payment link by SMS and e-mail', ar: 'أرسلت Tap رابط الدفع عبر رسالة نصية وبريد إلكتروني' });
      case 'pos_sms':
        return t({ en: 'The payment link was sent by SMS', ar: 'تم إرسال رابط الدفع عبر رسالة نصية' });
      default:
        return t({ en: 'The link could not be sent automatically — share it with the customer', ar: 'تعذّر إرسال الرابط تلقائيًا — شاركه مع العميل' });
    }
  };

  const watchedStatus = watching?.payment.status ?? null;

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div
        className="appt-dialog flex max-h-[95vh] w-full flex-col rounded-t-2xl bg-white shadow-xl sm:max-w-lg sm:rounded-2xl dark:bg-slate-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-none items-start justify-between border-b border-slate-200 px-4 py-3 sm:px-6 dark:border-slate-700">
          <div>
            <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100">{t({ en: 'Collect a deposit', ar: 'تحصيل عربون' })}</h2>
            {customerName && <p className="text-sm text-slate-500">{customerName}</p>}
          </div>
          <button className="text-slate-400 hover:text-slate-600" onClick={onClose} aria-label={t({ en: 'Close', ar: 'إغلاق' })}>✕</button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 sm:px-6">
          {/* The money picture, always the server's figures. */}
          <div className="rounded-xl border border-slate-200 p-3 dark:border-slate-700">
            <Row label={t({ en: 'Total session amount', ar: 'إجمالي الجلسة' })} value={fmtMoney(summary.total)} strong />
            <Row label={t({ en: 'Paid', ar: 'المدفوع' })} value={fmtMoney(summary.paid)} tone="ok" />
            {summary.pending > 0 && (
              <Row label={t({ en: 'Awaiting payment (link sent)', ar: 'بانتظار الدفع (تم إرسال الرابط)' })} value={fmtMoney(summary.pending)} tone="warn" />
            )}
            <Row label={t({ en: 'Remaining balance', ar: 'المبلغ المتبقي' })} value={fmtMoney(summary.remaining)} strong />
          </div>

          {watching ? (
            /* ── A link is out: show where it went and watch it ── */
            <div className="mt-3 rounded-xl border border-slate-200 p-3 dark:border-slate-700">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                  {t({ en: 'Tap payment link', ar: 'رابط دفع Tap' })} · {fmtMoney(watching.payment.amount)}
                </span>
                <StatusBadge status={watching.payment.status} />
              </div>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                {viaText(watching.via)}
                {watching.payment.notified_to ? ` · ${watching.payment.notified_to}` : ''}
              </p>
              {watchedStatus === 'pending' && (
                <p className="mt-1 text-xs text-slate-400">
                  {t({
                    en: 'Waiting for the customer to pay… this screen checks every few seconds, and the server keeps checking after you close it.',
                    ar: 'بانتظار سداد العميل… تتحقق هذه الشاشة كل بضع ثوانٍ، ويواصل الخادم التحقق بعد إغلاقها.',
                  })}
                </p>
              )}
              {watchedStatus === 'paid' && (
                <p className="mt-1 text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                  {t({ en: 'Paid — the deposit is recorded on the appointment.', ar: 'تم الدفع — سُجِّل العربون على الموعد.' })}
                  {watching.payment.loyalty_advance_points > 0 &&
                    ` ${t({ en: `${watching.payment.loyalty_advance_points} loyalty points added.`, ar: `تمت إضافة ${watching.payment.loyalty_advance_points} نقطة ولاء.` })}`}
                </p>
              )}
              {(watchedStatus === 'expired' || watchedStatus === 'cancelled' || watchedStatus === 'failed') && (
                <p className="mt-1 text-sm font-semibold text-red-600">
                  {watchedStatus === 'expired'
                    ? t({ en: 'The link expired without payment.', ar: 'انتهت صلاحية الرابط دون سداد.' })
                    : watchedStatus === 'cancelled'
                      ? t({ en: 'The link was cancelled.', ar: 'تم إلغاء الرابط.' })
                      : t({ en: 'The payment failed.', ar: 'فشلت عملية الدفع.' })}
                </p>
              )}
              {watching.payment.payment_url && watchedStatus === 'pending' && (
                <div className="mt-2 flex items-center gap-2">
                  <input readOnly className="appt-input flex-1 text-xs" value={watching.payment.payment_url} onFocus={(e) => e.currentTarget.select()} />
                  <button type="button" className="appt-btn-ghost text-xs" onClick={() => void copyLink()}>
                    {copied ? t({ en: 'Copied', ar: 'تم النسخ' }) : t({ en: 'Copy link', ar: 'نسخ الرابط' })}
                  </button>
                </div>
              )}
              {watchedStatus === 'pending' && (
                <div className="mt-3 flex flex-wrap gap-2">
                  <button type="button" className="appt-btn-ghost" disabled={saving} onClick={() => void resend()}>
                    {t({ en: 'Send again', ar: 'إعادة الإرسال' })}
                  </button>
                  <button type="button" className="appt-btn-ghost text-red-600" disabled={saving} onClick={() => void cancelLink()}>
                    {t({ en: 'Cancel link', ar: 'إلغاء الرابط' })}
                  </button>
                </div>
              )}
              {watchedStatus !== 'pending' && (
                <button type="button" className="mt-3 appt-btn-ghost" onClick={() => setWatching(null)}>
                  {t({ en: 'Collect another payment', ar: 'تحصيل دفعة أخرى' })}
                </button>
              )}
            </div>
          ) : (
            /* ── Ask for a new deposit ── */
            <>
              <div className="mt-3">
                <label className="appt-label">{t({ en: 'Deposit amount', ar: 'مبلغ العربون' })}</label>
                <input
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step={step}
                  className="appt-input text-lg font-semibold tabular-nums"
                  value={amountStr}
                  onChange={(e) => setAmountStr(e.target.value)}
                  disabled={saving || askable <= 0}
                  placeholder={fmtMoney(0)}
                />
                {quick.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {quick.map((v, i) => (
                      <button key={v} type="button" className="appt-btn-ghost text-xs" onClick={() => setAmountStr(fmtMoney(v))} disabled={saving}>
                        {[25, 50, 100][i] === 100 || v === askable ? t({ en: 'Full balance', ar: 'كامل المتبقي' }) : `${[25, 50, 100][i]}%`} · {fmtMoney(v)}
                      </button>
                    ))}
                  </div>
                )}
                {askable <= 0 && (
                  <p className="mt-1 text-xs text-slate-400">
                    {summary.pending > 0
                      ? t({ en: 'A link for the whole remaining balance is already out.', ar: 'هناك رابط بكامل المبلغ المتبقي قيد الانتظار بالفعل.' })
                      : t({ en: 'Nothing is left to collect.', ar: 'لا يوجد مبلغ متبقٍ للتحصيل.' })}
                  </p>
                )}
              </div>

              {/* Tap: the link by SMS */}
              <div className="mt-4 rounded-xl border border-slate-200 p-3 dark:border-slate-700">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  {t({ en: 'Send a payment link (Tap)', ar: 'إرسال رابط دفع (Tap)' })}
                </div>
                {tapType ? (
                  <>
                    <p className="mt-1 text-xs text-slate-500">
                      {t({
                        en: 'Tap texts the customer a link; the deposit is recorded once they pay. No SMS is sent by the POS.',
                        ar: 'ترسل Tap للعميل رابطًا برسالة نصية، ويُسجَّل العربون عند السداد. لا يرسل نظام نقاط البيع أي رسالة.',
                      })}
                    </p>
                    <div className="mt-2 space-y-2">
                      <div>
                        <label className="appt-label">{t({ en: 'Mobile number', ar: 'رقم الجوال' })}</label>
                        <div className="flex gap-2" dir="ltr">
                          <select
                            className="appt-input w-40 flex-none"
                            value={dialCode}
                            onChange={(e) => setDialCode(e.target.value)}
                            disabled={saving}
                            aria-label={t({ en: 'Country code', ar: 'رمز الدولة' })}
                          >
                            {dialChoices.map((c) => (
                              <option key={c.code} value={c.code}>+{c.code} {lang === 'ar' ? c.ar : c.en}</option>
                            ))}
                          </select>
                          <input
                            className="appt-input flex-1"
                            inputMode="tel"
                            value={localNumber}
                            onChange={(e) => setLocalNumber(e.target.value)}
                            disabled={saving}
                            placeholder={t({ en: 'local number', ar: 'الرقم المحلي' })}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="appt-label">{t({ en: 'E-mail (optional)', ar: 'البريد الإلكتروني (اختياري)' })}</label>
                        <input className="appt-input" type="email" dir="ltr" value={email} onChange={(e) => setEmail(e.target.value)} disabled={saving} />
                      </div>
                    </div>
                    <button
                      type="button"
                      className="appt-btn-primary mt-3 w-full"
                      disabled={saving || askable <= 0 || entered <= 0}
                      onClick={() => void create('tap_invoice', tapType)}
                    >
                      {saving ? t({ en: 'Sending…', ar: 'جارٍ الإرسال…' }) : `${t({ en: 'Send link', ar: 'إرسال الرابط' })}${entered > 0 ? ` · ${fmtMoney(entered)}` : ''}`}
                    </button>
                  </>
                ) : (
                  <p className="mt-1 text-xs text-slate-400">
                    {t({ en: 'Tap is not enabled for this company.', ar: 'خدمة Tap غير مفعّلة لهذه الشركة.' })}
                  </p>
                )}
              </div>

              {/* Money taken at the desk */}
              <div className="mt-4 rounded-xl border border-slate-200 p-3 dark:border-slate-700">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  {t({ en: 'Paid at the desk', ar: 'مدفوع في الاستقبال' })}
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  {t({ en: 'Recorded as paid immediately. It is booked on this method when the invoice is issued.', ar: 'يُسجَّل كمدفوع فورًا، ويُقيَّد على هذه الطريقة عند إصدار الفاتورة.' })}
                </p>
                <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {deskMethods.map((m) => (
                    <button
                      key={m.code}
                      type="button"
                      className="appt-btn-ghost justify-center"
                      disabled={saving || askable <= 0 || entered <= 0}
                      onClick={() => void create('manual', m)}
                      title={t({ en: `Record ${fmtMoney(entered)} on ${m.name}`, ar: `تسجيل ${fmtMoney(entered)} على ${m.name}` })}
                    >
                      {lang === 'ar' && m.name_ar ? m.name_ar : m.name}
                    </button>
                  ))}
                  {deskMethods.length === 0 && typesQ.isSuccess && (
                    <p className="col-span-full text-xs text-slate-400">{t({ en: 'No payment methods configured.', ar: 'لا توجد طرق دفع معدّة.' })}</p>
                  )}
                </div>
              </div>

              <div className="mt-3">
                <label className="appt-label">{t({ en: 'Note (optional)', ar: 'ملاحظة (اختياري)' })}</label>
                <input className="appt-input" value={note} onChange={(e) => setNote(e.target.value)} disabled={saving} maxLength={500} />
              </div>
            </>
          )}

          {notice && (
            <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800 dark:bg-amber-950 dark:text-amber-200">{notice}</div>
          )}
          {error && (
            <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-700 dark:bg-red-950 dark:text-red-200">{error}</div>
          )}
        </div>

        <div className="appt-dialog-footer flex flex-none gap-2 border-t border-slate-200 px-4 pt-3 sm:px-6 sm:pt-4 dark:border-slate-700">
          <button className="appt-btn-ghost flex-1 sm:ms-auto sm:flex-none" onClick={onClose} disabled={saving}>
            {t({ en: 'Done', ar: 'تم' })}
          </button>
        </div>
      </div>
    </div>
  );
}

export function paymentStatusText(status: AppointmentPayment['status'], t: (m: { en: string; ar: string }) => string): string {
  switch (status) {
    case 'pending':
      return t({ en: 'Awaiting payment', ar: 'بانتظار الدفع' });
    case 'paid':
      return t({ en: 'Paid', ar: 'مدفوع' });
    case 'applied':
      return t({ en: 'Paid · on invoice', ar: 'مدفوع · على الفاتورة' });
    case 'failed':
      return t({ en: 'Failed', ar: 'فشل' });
    case 'cancelled':
      return t({ en: 'Cancelled', ar: 'ملغى' });
    case 'expired':
      return t({ en: 'Expired', ar: 'منتهي الصلاحية' });
    case 'refunded':
      return t({ en: 'Refunded', ar: 'مسترد' });
    default:
      return status;
  }
}

export function StatusBadge({ status }: { status: AppointmentPayment['status'] }): JSX.Element {
  const { t } = useLang();
  const tone =
    status === 'paid' || status === 'applied'
      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200'
      : status === 'pending'
        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
        : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300';
  return <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${tone}`}>{paymentStatusText(status, t)}</span>;
}

function Row({ label, value, strong, tone }: { label: string; value: string; strong?: boolean; tone?: 'ok' | 'warn' }): JSX.Element {
  const color =
    tone === 'ok'
      ? 'text-emerald-700 dark:text-emerald-300'
      : tone === 'warn'
        ? 'text-amber-700 dark:text-amber-300'
        : 'text-slate-700 dark:text-slate-200';
  return (
    <div className={`flex items-center justify-between py-0.5 text-sm ${strong ? 'font-bold' : ''} ${color}`}>
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}
